import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { FadeIn } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Phone, Instagram, Facebook } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subject || !form.message) {
      toast.error("Please fill in name, email, subject and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry received. We'll write back soon.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="contact-page" className="pt-32 pb-24">
      <section className="mx-auto max-w-[1600px] px-6 lg:px-12">
        <FadeIn>
          <div className="stamp mb-8">Vol · VII · Correspondence</div>
          <h1 className="font-display text-6xl md:text-8xl leading-[0.95] text-[#F2E7D0] max-w-5xl">
            Write to <em className="italic text-[#D97645]">us</em>.
          </h1>
        </FadeIn>
      </section>

      <section className="mx-auto max-w-[1600px] px-6 lg:px-12 py-16 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <FadeIn className="lg:col-span-5">
          <div className="chapter-num mb-4">Estate</div>
          <p className="font-display text-4xl leading-tight text-[#F2E7D0]">
            Tikratoli Farm Resorts & Banquets,<br/>
            Satish Chowk, Gumla Road,<br/>
            Tikratoli, Ranchi — 835303,<br/>
            Jharkhand, India
          </p>
          <p
            data-testid="contact-drive-note"
            className="mt-4 text-xs font-mono-label uppercase tracking-wider text-[#D97645] border-l-2 border-[#D97645] pl-3 leading-relaxed"
          >
            20 min drive from Piska More &amp; 30 min drive from Kutchery Chowk, Ranchi.
          </p>
          <div className="mt-10 space-y-4 text-[#B8A891] text-sm">
            <div>
              <div className="font-mono-label text-[#D97645]">Call · Rahul</div>
              <a href="tel:+917004810637" className="text-[#F2E7D0] hover:text-[#D97645] transition inline-flex items-center gap-2" data-testid="contact-phone">
                <Phone size={14} /> +91 70048 10637
              </a>
            </div>
            <div>
              <div className="font-mono-label text-[#D97645]">Reception</div>
              <div>Rahul@tikratoliestate.com</div>
            </div>
            <div>
              <div className="font-mono-label text-[#D97645]">Reservations</div>
              <div>TikratoliBookings@gmail.com</div>
            </div>
            <div>
              <div className="font-mono-label text-[#D97645] mb-2">Follow</div>
              <div className="flex items-center gap-4">
                <a
                  href="https://instagram.com/tikratoli_farm_resorts/"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-instagram"
                  aria-label="Instagram"
                  className="w-10 h-10 border border-white/20 hover:border-[#D97645] hover:text-[#D97645] flex items-center justify-center text-[#F2E7D0] transition"
                >
                  <Instagram size={16} />
                </a>
                <a
                  href="https://facebook.com/TikratoliBanquets"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="social-facebook"
                  aria-label="Facebook"
                  className="w-10 h-10 border border-white/20 hover:border-[#D97645] hover:text-[#D97645] flex items-center justify-center text-[#F2E7D0] transition"
                >
                  <Facebook size={16} />
                </a>
              </div>
            </div>
          </div>

          {/* Google Maps embed (no API key required — public share URL) */}
          <div className="mt-10 border border-white/10">
            <iframe
              data-testid="contact-map"
              title="Tikratoli Farm Resorts location"
              src="https://www.google.com/maps?q=Tikratoli+Estate+%26+Banquets%2C+Satish+Chowk%2C+Gumla+Rd%2C+Tikratoli%2C+Jharkhand+835303&output=embed"
              width="100%"
              height="280"
              style={{ border: 0, filter: "grayscale(0.4) contrast(1.05) brightness(0.85)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
        </FadeIn>

        <FadeIn delay={0.1} className="lg:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-1">
              <Label className="font-mono-label text-[#B8A891]">Name</Label>
              <Input data-testid="contact-name" value={form.name} onChange={change("name")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div className="md:col-span-1">
              <Label className="font-mono-label text-[#B8A891]">Email</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={change("email")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div className="md:col-span-1">
              <Label className="font-mono-label text-[#B8A891]">Phone</Label>
              <Input data-testid="contact-phone" value={form.phone} onChange={change("phone")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div className="md:col-span-1">
              <Label className="font-mono-label text-[#B8A891]">Subject</Label>
              <Input data-testid="contact-subject" value={form.subject} onChange={change("subject")} className="mt-2 bg-transparent border-white/20 rounded-none h-12" />
            </div>
            <div className="md:col-span-2">
              <Label className="font-mono-label text-[#B8A891]">Message</Label>
              <Textarea data-testid="contact-message" rows={6} value={form.message} onChange={change("message")} className="mt-2 bg-transparent border-white/20 rounded-none" />
            </div>
            <div className="md:col-span-2">
              <Button
                data-testid="contact-submit"
                type="submit"
                disabled={loading}
                className="rounded-none bg-[#D97645] hover:bg-[#C0602F] text-[#F2E7D0] font-mono-label h-14 px-8"
              >
                {loading ? "Sending…" : "Send enquiry"}
              </Button>
            </div>
          </form>
        </FadeIn>
      </section>
    </main>
  );
}
